import { Link } from "react-router-dom";
import useReveal from "../../components/useReveal";
import Seo from "../../components/Seo";
import ArticleGrid from "./ArticleGrid";
import NewsletterForm from "./NewsletterForm";

// ── Main page ─────────────────────────────────────────────────────────────────
export default function NewsPage() {
  useReveal();

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Ateno Research",
    url: "https://ateno.co/research",
    description: "Product updates, engineering notes, and spatial intelligence research from the Ateno team.",
  };

  return (
    <div className="research-page">
      <Seo
        title="Research"
        description="Product updates, engineering notes, and spatial intelligence research from the Ateno team."
        path="/research"
        type="website"
        jsonLd={blogJsonLd}
      />
      {/* Hero */}
      <section className="section section-dark research-hero" id="spatial-intelligence">
        <div className="container" style={{textAlign:"center"}}>
          <div className="reveal eyebrow">Research</div>
          <h1 className="reveal headline" style={{marginBottom:20}}>Spatial intelligence research.</h1>
          <p className="reveal subhead" style={{maxWidth:600, margin:"0 auto"}}>Publications, technical reports, and work in progress from the Ateno team.</p>
        </div>
      </section>

      <section className="section section-dark research-focus">
        <div className="container">
          <div className="reveal eyebrow research-section-label">Focus areas</div>
          <div className="research-topic-grid">
            {[
              {
                title: "Technical reports",
                desc: "Focused writeups on reconstruction, geometry, and system design.",
                image: "/assets/research/tech-report.webp",
              },
              {
                title: "Reconstruction",
                desc: "Experiments and prototypes that inform the product roadmap.",
                image: "/assets/research/reconstruction.webp",
                id: "reconstruction",
              },
              {
                title: "Hardware research",
                desc: "Acceleration work and compute experiments for spatial AI.",
                image: "/assets/home/chip-on-board.png",
                id: "hardware-research",
              },
            ].map((topic, index) => (
              <article
                key={topic.title}
                id={topic.id}
                className={`reveal reveal-d${index + 1} research-topic-card`}
              >
                <div className="research-topic-card-image" style={{ backgroundImage: `url(${topic.image})` }} />
                <div className="research-topic-card-body">
                  <h3>{topic.title}</h3>
                  <p>{topic.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Published work */}
      <section className="section section-dark research-papers" id="papers">
        <div className="container">
          <div className="reveal eyebrow research-section-label">Published work</div>
          <article className="reveal reveal-d1 research-paper">
            <div className="research-paper-meta">
              <span className="research-paper-badge">Paper</span>
              <span className="research-paper-date">May 2026 · ResearchGate</span>
            </div>
            <h3 className="research-paper-title">
              Ateno AI: Natural-Language Editing of Interactive 3D Object Scenes
            </h3>
            <p className="research-paper-abstract">
              A method for editing interactive 3D object scenes directly from natural-language instructions — the
              research foundation behind Ateno's spatial editing pipeline.
            </p>
            <div className="research-paper-divider" />
            <div className="research-paper-footer">
              <div className="research-paper-authors">
                {[
                  { name: "Artemii Mavlitov", role: "CTO", slug: "artemii-mavlitov" },
                  { name: "Sviatoslav Gladyshev", role: "CEO", slug: "sviatoslav-gladyshev" },
                ].map((author) => (
                  <Link key={author.slug} to={`/team/${author.slug}`} className="research-paper-author">
                    <span className="research-paper-author-name">{author.name}</span>
                    <span className="research-paper-author-role">{author.role}</span>
                  </Link>
                ))}
              </div>
              <a
                href="https://www.researchgate.net/publication/405323496_Ateno_AI_Natural-Language_Editing_of_Interactive_3D_Object_Scenes"
                target="_blank"
                rel="noopener noreferrer"
                className="research-paper-link"
              >
                Read on ResearchGate →
              </a>
            </div>
          </article>
        </div>
      </section>

      {/* Article grid — the section (and its #publications anchor, which the
          subnav links to) always renders; only the empty-state message
          inside it is compact instead of a full-height block, so there's
          no dead space but the anchor never goes missing. */}
      <section className="section section-dark research-latest" id="publications">
        <div className="container">
          <h2 className="reveal research-section-label">Industry analysis</h2>
          <p className="reveal subhead">Essays on semiconductor markets and AI infrastructure, separate from the research paper above.</p>
          <ArticleGrid basePath="/research" emptyMessage="No articles published yet — check back soon." />
        </div>
      </section>

      {/* Newsletter */}
      <section className="section section-dark research-newsletter-section" style={{ paddingBottom: 80 }}>
        <div className="container">
          <div className="reveal card-dark newsletter-card">
            <div className="newsletter-card-inner">
              <NewsletterForm source="research-page" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
